import { animate, style, transition, trigger } from '@angular/animations';
import {
  AfterContentInit,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  inject,
  Input,
  NgZone,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { BurgerMenuContent, CategoryBurgerLink, Link, LinkContent, SubNavigationContent } from 'src/app/models';
import { CategoryService } from 'src/app/services/category/category.service';
import { ContentService, sections } from 'src/app/services/content/content.service';
import { ShippingDialogService } from 'src/app/services/dialog/shipping-dialog.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { environment } from 'src/environments/environment';
import { ContentSection } from '../content-section';

@Component({
  selector: 'app-meny-navigation',
  templateUrl: './meny-navigation.component.html',
  styleUrls: ['./meny-navigation.component.scss'],
  animations: [
    trigger('installAnimation', [
      transition(':enter', [style({ opacity: 0, width: 0 }), animate('800ms ease-out', style({ opacity: 1, width: '*' }))]),
      transition(':leave', [style({ opacity: 1, width: '*' }), animate('800ms ease-in', style({ opacity: 0, width: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, RouterLinkActive, MatIconModule],
})
export class MenyNavigationComponent extends ContentSection implements AfterContentInit {
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);
  private shippingDialogService = inject(ShippingDialogService);
  private categoryService = inject(CategoryService);
  private document = inject<Document>(DOCUMENT);
  private ngZone = inject(NgZone);

  contentUrl = `${environment.urls.content.merchant_content}/`;

  @Input() showShipping: boolean;

  hideGroceries: boolean;
  groceries: Link;

  links: (LinkContent | SubNavigationContent | BurgerMenuContent | CategoryBurgerLink)[];
  openNavigationName?: string;
  openNavigationRef: HTMLElement;
  closeSubNavTimeout: any;

  @HostListener('focusout', ['$event']) onFocusOut(event: FocusEvent) {
    if (!this.openNavigationRef?.contains(event.relatedTarget as any) && this.document.hasFocus()) {
      this.closeNavigation();
    }
  }

  @HostListener('mouseenter', ['$event']) onMouseEnter(event: MouseEvent) {
    clearTimeout(this.closeSubNavTimeout);
  }

  @HostListener('mouseleave', ['$event']) onMouseLeave(event: MouseEvent) {
    this.ngZone.runOutsideAngular(() => {
      this.closeSubNavTimeout = setTimeout(() => {
        this.closeNavigation();
      }, 2000);
    });
  }

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, sections.header);

    this.hideGroceries = this.contentService.getMerchantId() === 17;
    this.groceries = { url: environment.urls.products, name: this.content.get('groceries') };

    const merchantContent = this.contentService.getMerchantContent();
    this.links = [];
    for (const entry of merchantContent) {
      switch (entry.type) {
        case 'simple': {
          const link = this.contentService.getContentLink(entry);
          this.links.push(link);
          break;
        }
        case 'categoryBurger': {
          const link = this.categoryService.buildCategoryBurgerMenu(entry);
          if (link) this.links.push(link);
          break;
        }
        case 'link':
        case 'subNav':
        case 'burger':
          this.links.push(entry);
          break;
      }
    }
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  openShipping() {
    this.shippingDialogService.openShippingDialog();
  }

  openNavigation(el: HTMLElement, navigation: string) {
    if (this.openNavigationRef) {
      this.openNavigationRef.blur();
    }

    this.openNavigationName = navigation;
    this.openNavigationRef = el;

    this.ngZone.runOutsideAngular(() => setTimeout(() => el.focus(), 0));
  }

  closeNavigation() {
    this.openNavigationName = undefined;
  }

  sameSite(link: LinkContent) {
    return this.contentService.sameSite(link);
  }

  skipNavigationContent(e: MouseEvent) {
    e.preventDefault();
    const shipping = this.document.getElementById('shipping');
    if (shipping) {
      shipping.focus();
      return;
    }
  }
}
